import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../store/AuthContext";
import type {
  HelpCenterAudience,
  IHelpCenter,
  IHelpCenterSection,
} from "../../interfaces/models/IHelpCenter";
import Pencil from "../../components/Atomos/Icons/ViewMode/Pencil";
import HelpCenterAudienceTabs from "./HelpCenterAudienceTabs";
import HelpCenterSubSectionLink from "./HelpCenterSubSectionLink";
import {
  getMemberPortalAudiences,
  getVisibleHelpCenterAudiences,
  isHelpCenterStaff,
  isJwtHelpCenterAccessResolved,
  resolveHelpCenterPortal,
  resolveWsgPermission,
} from "./helpCenterPermissions";
import { helpCenterCategoryEditNewPath, helpCenterCategoryEditPath } from "./helpCenterRoutes";
import {
  buildHelpCenterSectionEntries,
  filterSectionEntriesByKeyword,
  filterSectionsByKeyword,
  getAudiencePage,
  getPublishedSections,
  getVisibleSubSections,
  groupSectionEntriesByAudience,
  HELP_CENTER_AUDIENCE_LABELS,
  HELP_CENTER_BROWSE_BY_TOPIC_TEXT,
  shouldShowViewAll,
  type HelpCenterSectionEntry,
} from "./helpCenterUtils";
import ChevronDownAccent from "../../components/Atomos/Icons/ChevronDownAccent";
import HelpCenterSearchIllustration from "./HelpCenterSearchIllustration";
import HelpCenterStaffSearchIllustration from "./HelpCenterStaffSearchIllustration";
import { useHelpCenterConfig } from "./useHelpCenterConfig";
import "./HelpCenter.css";

function HelpCenterSectionCard({
  audience,
  section,
  isSearchActive = false,
  isStaff = false,
}: {
  audience: HelpCenterAudience;
  section: IHelpCenterSection;
  isSearchActive?: boolean;
  isStaff?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const visibleSubSections = isSearchActive
    ? section.subSections
    : getVisibleSubSections(section, expanded);
  const showViewAll = !isSearchActive && shouldShowViewAll(section);

  return (
    <section className="help-center-section">
      <div className="help-center-section-header">
        <div className="help-center-section-title-row">
          <h2 className="help-center-section-title">{section.title}</h2>
          {isStaff ? (
            <Link
              to={helpCenterCategoryEditPath(audience, section.id)}
              className="help-center-section-edit"
            >
              <span>Edit</span>
              <span className="help-center-section-edit-icon" aria-hidden="true">
                <Pencil width={16} height={16} color="currentColor" />
              </span>
            </Link>
          ) : null}
        </div>
      </div>
      <ul className="help-center-link-list">
        {visibleSubSections.map((subSection) => (
          <li key={subSection.id}>
            <HelpCenterSubSectionLink
              audience={audience}
              sectionId={section.id}
              subSection={subSection}
            />
          </li>
        ))}
      </ul>
      {showViewAll ? (
        <button
          type="button"
          className="help-center-view-toggle"
          onClick={() => setExpanded((value) => !value)}
        >
          <span>{expanded ? "View less" : "View all"}</span>
          <span
            className={`help-center-view-toggle-icon${
              expanded ? " help-center-view-toggle-icon-expanded" : ""
            }`}
          >
            <ChevronDownAccent />
          </span>
        </button>
      ) : null}
    </section>
  );
}

function HelpCenterSectionsGrid({
  entries,
  isSearchActive,
  isStaff,
}: {
  entries: HelpCenterSectionEntry[];
  isSearchActive: boolean;
  isStaff: boolean;
}) {
  return (
    <div className="help-center-grid">
      {entries.map((entry) => (
        <HelpCenterSectionCard
          key={`${entry.audience}-${entry.section.id}`}
          audience={entry.audience}
          section={entry.section}
          isSearchActive={isSearchActive}
          isStaff={isStaff}
        />
      ))}
    </div>
  );
}

function getIntroForMemberPortal(
  config: IHelpCenter,
  memberAudiences: HelpCenterAudience[],
): string | undefined {
  for (const audience of memberAudiences) {
    const intro = getAudiencePage(config, audience)?.intro?.trim();
    if (intro) {
      return intro;
    }
  }

  return undefined;
}

function scrollToMemberAudienceSection(audience: HelpCenterAudience) {
  document
    .getElementById(`help-center-audience-${audience}`)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function HelpCenterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tokenDecoded, loggedPerson, isAuthenticated } = useAuth();
  const { config, error, isReady } = useHelpCenterConfig();
  const [activeAudience, setActiveAudience] =
    useState<HelpCenterAudience>("members");
  const [searchQuery, setSearchQuery] = useState("");

  const permission = resolveWsgPermission(
    tokenDecoded?.extension_WSG_Permissions,
  );

  const visibleAudiences = useMemo(
    () =>
      getVisibleHelpCenterAudiences(permission, loggedPerson?.firmAdminAccess),
    [permission, loggedPerson?.firmAdminAccess],
  );

  const isStaff = isHelpCenterStaff(permission);
  const portal = resolveHelpCenterPortal(searchParams, isStaff);
  const isStaffPortal = portal === "staff";
  const memberAudiences = useMemo(() => getMemberPortalAudiences(), []);
  const staffPortalAudiences = visibleAudiences;

  const isUserAccessReady =
    !isAuthenticated ||
    loggedPerson !== undefined ||
    isJwtHelpCenterAccessResolved(permission);

  useEffect(() => {
    if (!isStaffPortal) {
      return;
    }

    if (!staffPortalAudiences.includes(activeAudience)) {
      setActiveAudience(staffPortalAudiences[0] ?? "members");
    }
  }, [activeAudience, isStaffPortal, staffPortalAudiences]);

  useEffect(() => {
    setSearchQuery("");
  }, [activeAudience, portal]);

  const audiencePage =
    isStaffPortal && config ? getAudiencePage(config, activeAudience) : null;
  const staffSections = audiencePage ? getPublishedSections(audiencePage) : [];
  const memberSectionEntries = useMemo(() => {
    if (!config || isStaffPortal) {
      return [];
    }

    return buildHelpCenterSectionEntries(config, memberAudiences);
  }, [config, isStaffPortal, memberAudiences]);

  const isSearchActive = searchQuery.trim().length > 0;
  const displayedStaffSections = useMemo(
    () => filterSectionsByKeyword(staffSections, searchQuery),
    [staffSections, searchQuery],
  );
  const displayedMemberSectionEntries = useMemo(
    () => filterSectionEntriesByKeyword(memberSectionEntries, searchQuery),
    [memberSectionEntries, searchQuery],
  );

  const displayedMemberAudienceGroups = useMemo(
    () =>
      groupSectionEntriesByAudience(
        displayedMemberSectionEntries,
        memberAudiences,
      ),
    [displayedMemberSectionEntries, memberAudiences],
  );

  const intro = isStaffPortal
    ? audiencePage?.intro
    : config
      ? getIntroForMemberPortal(config, memberAudiences)
      : undefined;
  const hasContent = isStaffPortal
    ? Boolean(config && audiencePage)
    : Boolean(config && memberSectionEntries.length > 0);
  const showEmptyAudienceMessage =
    isReady &&
    !error &&
    config &&
    (isStaffPortal ? !audiencePage : memberSectionEntries.length === 0);

  const showMemberAudienceJumpNav = !isStaffPortal && hasContent;

  const handleAddNewCategory = () => {
    if (!audiencePage || !isStaff || !isStaffPortal) {
      return;
    }

    navigate(helpCenterCategoryEditNewPath(activeAudience));
  };

  return (
    <div className="w-full pb-20">
      <div className="w-full bg-white help-center-shell">
        <div className="help-center-content">
          {isStaffPortal && isStaff && isUserAccessReady ? (
            <HelpCenterAudienceTabs
              audiences={staffPortalAudiences}
              activeAudience={activeAudience}
              onChange={setActiveAudience}
              staffTheme={isStaff}
              className="help-center-audience-tabs-top"
            />
          ) : isStaffPortal ? (
            <div
              className="help-center-audience-tabs help-center-audience-tabs-pending help-center-audience-tabs-top"
              aria-hidden="true"
            />
          ) : null}

          <h1
            className={`help-center-page-title${
              isStaffPortal ? " help-center-page-title-staff" : ""
            }`}
          >
            WSG Help Center
          </h1>

          {hasContent && intro ? (
            <header className="help-center-header help-center-fade-in">
              <p className="help-center-intro">{intro}</p>
            </header>
          ) : null}

          <div className="help-center-browse-row">
            <section className="help-center-search-block">
              <h2 className="help-center-block-title">Browse by keyword</h2>
              <div className="help-center-search-banner">
                <label className="help-center-search-field">
                  <input
                    type="search"
                    className="help-center-search-input"
                    value={searchQuery}
                    placeholder="Search topics, resources, guides..."
                    disabled={!hasContent}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                </label>
                <div
                  className={`help-center-search-illustration${
                    isStaffPortal ? " help-center-search-illustration--staff" : ""
                  }`}
                  aria-hidden="true"
                >
                  {isStaffPortal ? (
                    <HelpCenterStaffSearchIllustration />
                  ) : (
                    <HelpCenterSearchIllustration />
                  )}
                </div>
              </div>
            </section>

            <section className="help-center-topic-block">
              <h2 className="help-center-block-title">Browse by topic</h2>
              <p className="help-center-topic-description">
                {HELP_CENTER_BROWSE_BY_TOPIC_TEXT}
              </p>
            </section>
          </div>

          {isStaffPortal && isStaff && isUserAccessReady ? (
            <div className="help-center-topic-divider help-center-add-category-row">
              <hr className="help-center-topic-divider-line" aria-hidden="true" />
              <button
                type="button"
                className="help-center-add-category-button"
                disabled={!hasContent}
                onClick={() => void handleAddNewCategory()}
              >
                <span>Add New Category</span>
                <span className="help-center-add-category-button-icon" aria-hidden="true">
                  +
                </span>
              </button>
            </div>
          ) : showMemberAudienceJumpNav ? (
            <nav
              className="help-center-member-jump-nav"
              aria-label="Jump to help center section"
            >
              {memberAudiences.map((audience, index) => (
                <Fragment key={audience}>
                  {index > 0 ? (
                    <span
                      className="help-center-member-jump-separator"
                      aria-hidden="true"
                    >
                      |
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="help-center-member-jump-link"
                    onClick={() => scrollToMemberAudienceSection(audience)}
                  >
                    {HELP_CENTER_AUDIENCE_LABELS[audience]}
                  </button>
                </Fragment>
              ))}
            </nav>
          ) : null}

          {error && isReady && !config ? (
            <div className="help-center-sections">
              <p className="help-center-status help-center-status-error">
                {error}
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : null}

          {showEmptyAudienceMessage ? (
            <div className="help-center-sections help-center-fade-in">
              <p className="help-center-status help-center-status-error">
                Help Center content is not configured for this audience yet.
              </p>
            </div>
          ) : null}

          {hasContent ? (
            <div className="help-center-sections help-center-fade-in">
              {isStaffPortal ? (
                displayedStaffSections.length > 0 ? (
                  <HelpCenterSectionsGrid
                    entries={displayedStaffSections.map((section) => ({
                      audience: activeAudience,
                      section,
                    }))}
                    isSearchActive={isSearchActive}
                    isStaff={isStaff}
                  />
                ) : isReady ? (
                  <p className="help-center-status">
                    {isSearchActive
                      ? "No help topics match your search."
                      : "No help topics are available for this audience yet."}
                  </p>
                ) : null
              ) : displayedMemberAudienceGroups.length > 0 ? (
                <div className="help-center-member-groups">
                  {displayedMemberAudienceGroups.map((group, groupIndex) => (
                    <Fragment key={group.audience}>
                      {groupIndex > 0 ? (
                        <hr
                          className="help-center-audience-group-separator"
                          aria-hidden="true"
                        />
                      ) : null}
                      <section
                        className="help-center-audience-group"
                        aria-labelledby={`help-center-audience-${group.audience}`}
                      >
                        <h2
                          id={`help-center-audience-${group.audience}`}
                          className="help-center-audience-group-title"
                        >
                          {HELP_CENTER_AUDIENCE_LABELS[group.audience]}
                        </h2>
                        <HelpCenterSectionsGrid
                          entries={group.entries}
                          isSearchActive={isSearchActive}
                          isStaff={false}
                        />
                      </section>
                    </Fragment>
                  ))}
                </div>
              ) : isReady ? (
                <p className="help-center-status">
                  {isSearchActive
                    ? "No help topics match your search."
                    : "No help topics are available for this audience yet."}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
